



















































































































































import Vue from 'vue';
import { cloneDeep, toString, toNumber } from 'lodash';
import { api, ACCOUNT_API_SUB_URLS, MEMBER_API_SUB_URLS, V2 } from '@/api';
import { DateFormat, FormValidation, TriggerRefreshError } from '@/mixins';
import { dateFormat, dateFormatYYYYMMDD, datePickerFormat, dialogMaxWidth } from '@/constants';
import { date, dateHelpers } from '@/validations';
import { req } from 'vuelidate/lib/validators/common';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { DatePicker } from '@/components/form-components';
import moment from 'moment';
import {
  FullscreenSuccess,
  FullscreenError,
  FullscreenLoading,
} from '@/components/fullscreen-wrapper-components';
import InputMaskTypes from '@/directives/input-mask/input-mask-types';
import { ExtendMixins } from '@/services';
import {
  AddDepositFormModel, IrsContributionLimitsResponseModel,
  MemberAccountResponseModel,
  MemberBankAccountResponseModel,
  SelectOption,
  GroupMigrationDates,
} from '@/@types/member';

interface Data {
  canCancelModal: boolean;
  formModel: AddDepositFormModel;
  validatedResponse: any;
  showConfirmModal: boolean;
  showConfirmModalActions: boolean;
  modalError: string;
  showSubmitSuccessMessage: boolean;
  showSubmitErrorMessage: boolean;
  isLoading: boolean;
  recurringDateOptions: SelectOption[];
  dialogMaxWidth: string;
  currentDate: moment.Moment;
}

export default ExtendMixins(
  DateFormat,
  FormValidation,
  TriggerRefreshError,
).extend({
  name: 'ReviewDeposit',
  props: {
    accountId: {
      type: [Number, String],
      default: null,
    },
    isOneTime: {
      type: Boolean,
      default: true,
    },
    depositAmount: {
      type: [Number, String],
      default: 0,
    },
    selectedDeposit: {
      type: Object as () => any,
      default: null,
    },
    successMessage: {
      type: String,
      required: true,
    },
    errorMessage: {
      type: String,
      required: true,
    },
    showTaxYearNote: {
      type: Boolean,
      required: false,
      default: false,
    },
    taxYearNote: {
      type: String,
      required: false,
      default: '',
    },
  },
  data(): Data {
    return {
      canCancelModal: true,
      formModel: {
        amount: this.depositAmount,
        bankAccount: null,
        expectedPostDate: '',
        recurringDate: 5,
        taxYear: null,
        termsAndConditions: false,
      },
      validatedResponse: null,
      showConfirmModal: false,
      showConfirmModalActions: false,
      modalError: '',
      showSubmitSuccessMessage: false,
      showSubmitErrorMessage: false,
      isLoading: false,
      recurringDateOptions: [
        {
          text: this.$options.filters.t('make-deposit_option_1st-of-month'),
          value: 1,
        },
        {
          text: this.$options.filters.t('make-deposit_option_5th-of-month'),
          value: 5,
        },
        {
          text: this.$options.filters.t('make-deposit_option_10th-of-month'),
          value: 10,
        },
        {
          text: this.$options.filters.t('make-deposit_option_15th-of-month'),
          value: 15,
        },
        {
          text: this.$options.filters.t('make-deposit_option_25th-of-month'),
          value: 25,
        },
      ],
      dialogMaxWidth,
      currentDate: moment(),
    };
  },
  beforeMount() {
    if (this.selectedDeposit) {
      this.formModel.amount = this.selectedDeposit.amount;
      this.formModel.bankAccount = toString(this.selectedDeposit.bankId);
      if (this.isOneTime) {
        this.formModel.expectedPostDate = moment(this.selectedDeposit.pullDate, dateFormatYYYYMMDD).format(dateFormat);
      }
      this.formModel.recurringDate = parseInt(
        moment(this.selectedDeposit.pullDate, dateFormatYYYYMMDD).format('D'),
      );
      this.formModel.taxYear = this.selectedDeposit.taxYear;
    } else {
      this.setFormModel();
    }
    this.constructShowErrorModel('formModel');
  },
  watch: {
    depositAmount(val) {
      this.formModel.amount = val;
    },
  },
  validations: {
    formModel: {
      amount: {
        required,
        minValue(value): boolean {
          const val = InputMaskTypes.currency.deconstruct(value),
            valNum = parseFloat(val);
          return !req(value) || (!isNaN(valNum) && valNum > 0);
        },
        maxValue(value): boolean {
          const val = InputMaskTypes.currency.deconstruct(value),
            valNum = parseFloat(val);
          return !req(value) || (!isNaN(valNum) && valNum <= 999999999999.99);
        },
      },
      bankAccount: {
        required,
      },
      expectedPostDate: {
        required: requiredIf(function(this: any): boolean {
          return this.isOneTime;
        }),
        date,
        afterDefaultDate(value): boolean {
          if (this.defaultPostDate) {
            return dateHelpers.sameOrAfterDate(
              moment(this.defaultPostDate, dateFormat).format(dateFormat),
            )(value);
          }
          return true;
        },
        beforeTaxYearEnd(value): boolean {
          return dateHelpers.sameOrBeforeDate(
            moment(this.taxDayGracePeriod, dateFormat).format(dateFormat),
          )(value);
        },
        afterCurrentDate(value): boolean {
          return dateHelpers.afterDate(this.today.format(dateFormat))(value);
        },
        beforeGroupMigrationDate(value): boolean {
          if (this.groupMigrationDates && this.groupMigrationDates.hsaContributionDate) {
            return dateHelpers.beforeDate(
              moment(this.groupMigrationDates.hsaContributionDate, datePickerFormat).format(dateFormat),
            )(value);
          }
          return true;
        },
      },
      recurringDate: {
        required: requiredIf(function(this: any): boolean {
          return !this.isOneTime;
        }),
        beforeGroupMigrationDate(): boolean {
          if (!this.isOneTime && this.groupMigrationDates && this.groupMigrationDates.hsaContributionDate) {
            const firstPullDate = this.calculatePullDate();
            return dateHelpers.beforeDate(
              moment(this.groupMigrationDates.hsaContributionDate, datePickerFormat).format(dateFormat),
            )(firstPullDate);
          }
          return true;
        },
      },
      taxYear: {
        required: requiredIf(function(this: any): boolean {
          return this.isOneTime;
        }),
      },
      termsAndConditions: {
        required,
      },
    },
  },
  computed: {
    memberId(): string {
      return this.$store.getters.user.memberId;
    },
    today(): moment.Moment {
      return this.currentDate.startOf('day');
    },
    tomorrow(): string {
      return cloneDeep(this.today)
        .add(1, 'day')
        .format(dateFormat);
    },
    hsaAccount(): MemberAccountResponseModel {
      const hsaAccounts = this.$store.getters.activeHSAAccounts;
      hsaAccounts.sort((a, b) => {
        if (a.accountKey < b.accountKey) {
          return -1;
        }
        if (a.accountKey > b.accountKey) {
          return 1;
        }
        return 0;
      });
      return hsaAccounts[0];
    },
    irsLimits(): IrsContributionLimitsResponseModel[] {
      return this.$store.getters.irsLimits;
    },
    taxYears(): string[] {
      const VALID_TAX_YEAR_RECORDS = this.irsLimits.filter((taxYear) => {
        // Checking if the expected post date falls within tax year start & end dates
        const POST_DATE_BETWEEN_START_AND_END_DATES = moment(
          this.formModel.expectedPostDate,
          dateFormat,
        ).isBetween(
          moment(taxYear.startDate, dateFormat),
          moment(taxYear.endDate, dateFormat),
          null,
          '[]',
        );
        // Checking if the current date falls before or on the end date
        const CURRENT_DATE_IS_BEFORE_TAX_YEAR_END_DATE = this.today.isSameOrBefore(
          moment(moment(taxYear.endDate, dateFormat).startOf('day')),
        );
        // If the current date is NOT on or before tax day,
        // assume that the only records are ones where the expected post date
        // falls between the tax year start and end dates
        if (!CURRENT_DATE_IS_BEFORE_TAX_YEAR_END_DATE) {
          return POST_DATE_BETWEEN_START_AND_END_DATES;
        }
        // Tax year grace period is from the end date (tax day) til 5 days after the end date
        const TAX_YEAR_GRACE_PERIOD_END = moment(taxYear.endDate, dateFormat)
          .startOf('day')
          .add(5, 'days');
        // Checking if the expected post date falls within the tax year grace period
        const POST_DATE_WITHIN_GRACE_PERIOD = moment(
          this.formModel.expectedPostDate,
          dateFormat,
        )
          .startOf('day')
          .isBetween(
            moment(taxYear.endDate, dateFormat),
            moment(TAX_YEAR_GRACE_PERIOD_END, dateFormat),
            null,
            '[]',
          );
        return (
          POST_DATE_BETWEEN_START_AND_END_DATES || POST_DATE_WITHIN_GRACE_PERIOD
        );
      });
      const VALID_TAX_YEARS = VALID_TAX_YEAR_RECORDS.map((taxYearRecord) => {
        return taxYearRecord.taxYear;
      });
      return VALID_TAX_YEARS.sort((a, b) => {
        if (a < b) {
          return 1;
        } else if (a > b) {
          return -1;
        }
        return 0;
      });
    },
    selectedTaxYearModel(): IrsContributionLimitsResponseModel | any {
      return (
        this.irsLimits.find((limit) => {
          return limit.taxYear.toString() === this.formModel.taxYear.toString();
        }) || {}
      );
    },
    selectedTaxYearEndDate(): string {
      return this.selectedTaxYearModel.endDate;
    },
    taxDayGracePeriod(): string {
      return moment(this.selectedTaxYearEndDate, dateFormat)
        .add(5, 'days')
        .startOf('day')
        .format(dateFormat);
    },
    bankAccounts(): MemberBankAccountResponseModel[] {
      if (
        this.$store.getters.bankAccounts &&
        this.$store.getters.bankAccounts.length > 0
      ) {
        return this.$store.getters.bankAccountsForDeposit.map((account) => {
          return Object.assign(account, {
            text:
              account.name +
              this.$options.filters.t('make-deposit_account-label') +
              account.accountNumber.substr(account.accountNumber.length - 4),
            value: account.id,
          });
        });
      } else {
        return [];
      }
    },
    defaultPostDate(): string {
      if (this.hsaAccount) {
        if (
          moment(this.tomorrow, dateFormat).isSameOrAfter(
            moment(this.hsaAccount.startDate, dateFormat),
          )
        ) {
          return moment(this.tomorrow, dateFormat).format(dateFormat);
        }
        return moment(this.hsaAccount.startDate, dateFormat).format(dateFormat);
      }
      return '';
    },
    // VALIDATIONS
    amountErrors(): string[] {
      const formField = 'formModel.amount';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'make-deposit_form-error_amount_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_amount_min-value',
          'minValue',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_amount_max-value',
          'maxValue',
        ),
      ]);
    },
    expectedPostDateErrors(): string[] {
      const formField = 'formModel.expectedPostDate';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'make-deposit_form-error_expected-post-date_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          [
            'make-deposit_form-error_expected-post-date_date',
            'global_form-error_date_invalid-format',
          ],
          'date',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_after-default-date',
          'afterDefaultDate',
          { key: 'compareDate', val: 'compareDate' },
        ),
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_before-tax-year-end',
          'beforeTaxYearEnd',
          { key: 'compareDate', val: 'compareDate' },
        ),
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_after-current-date',
          'afterCurrentDate',
        ),
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_before-group-migration',
          'beforeGroupMigrationDate',
          { key: 'compareDate', val: 'compareDate' },
        ),
      ]);
    },
    recurringDateErrors(): string[] {
      const formField = 'formModel.recurringDate';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          'make-deposit_form-error_first-pull-before-group-migration',
          'beforeGroupMigrationDate',
          { key: 'compareDate', val: 'compareDate' },
        ),
      ]);
    },
    termsAndConditionsErrors(): string[] {
      const formField = 'formModel.termsAndConditions';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'make-deposit_form-error_terms-and-conditions_required',
            'global_form-error_terms-and-conditions_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    groupMigrationDates(): GroupMigrationDates {
      return this.$store.getters.groupMigrationDates;
    },
  },
  methods: {
    tryAgain(): void {
      this.$emit('updateConfirmBeforeClose', true);
      this.$emit('tryAgain');
      this.showSubmitSuccessMessage = false;
      this.showSubmitErrorMessage = false;
    },
    setFormModel(): void {
      this.formModel.bankAccount = toString(this.bankAccounts[0].id);
      this.formModel.expectedPostDate = this.defaultPostDate;
      this.formModel.taxYear = this.today.year();
    },
    closeWrapper(): void {
      this.$emit('closeWrapper');
    },
    calculatePullDate(): string {
      const recurringDate = moment().date(this.formModel.recurringDate);
      let pullDate = moment()
          .date(this.formModel.recurringDate)
          .format(dateFormat);
      if (this.today.isSameOrAfter(recurringDate)) {
        pullDate = cloneDeep(this.today)
          .add(1, 'month')
          .date(this.formModel.recurringDate)
          .format(dateFormat);
      }
      return pullDate;
    },
    setNumber(value): void {
      this.formModel.amount = parseFloat(value).toFixed(2);
    },
    deconstructExpectedPostDate(): void {
      this.formModel.expectedPostDate = InputMaskTypes.date.deconstruct(
        this.formModel.expectedPostDate,
      );
    },
    updatePostDate(postDate): void {
      this.formModel.expectedPostDate = this.formatDate(postDate);
      this.triggerBlur('formModel.expectedPostDate');
      this.updateHasChanged();
    },
    allowedPostDates(postDate): boolean {
      const dateFormatted = moment(postDate, datePickerFormat),
        dateIsSameOrAfterDefaultPostDate = dateFormatted.isSameOrAfter(
          moment(this.defaultPostDate, dateFormat),
        ),
        dateIsAfterCurrentDate = dateFormatted.isAfter(this.today),
        dateIsBeforeTaxDayGracePeriod = dateFormatted.isSameOrBefore(
          moment(this.taxDayGracePeriod, dateFormat),
        ),
        dateIsBeforeLastHSAContributionDate = this.groupMigrationDates && this.groupMigrationDates.hsaContributionDate ? dateFormatted.isBefore(
          moment(this.groupMigrationDates.hsaContributionDate, datePickerFormat),
        ) : true;
      return (
        dateIsSameOrAfterDefaultPostDate &&
        dateIsBeforeTaxDayGracePeriod &&
        dateIsAfterCurrentDate &&
        dateIsBeforeLastHSAContributionDate
      );
    },
    closeModal(): void {
      this.showConfirmModal = false;
    },
    validateDeposit(): void {
      const payload = {
        accountId: this.hsaAccount.accountId,
        amount: this.formModel.amount,
        taxYear: this.formModel.taxYear,
        isOneTime: this.isOneTime,
        pullDate: '',
        scheduledDepositId: this.selectedDeposit ? this.selectedDeposit.scheduledDepositId : null,
      };
      if (!this.isOneTime) {
        payload.pullDate = moment(this.calculatePullDate(), dateFormat).format(datePickerFormat);
        // Setting formModel as well so correct value is used when add deposit is called
        this.formModel.taxYear = payload.taxYear = moment(payload.pullDate, datePickerFormat).year();
      } else {
        payload.pullDate = moment(this.formModel.expectedPostDate, dateFormat).format(datePickerFormat);
      }
      this.isLoading = true;
      this.$emit('isLoading', true);
      // REST-057 - Validate Member Deposit
      api
        .get(
           `${MEMBER_API_SUB_URLS.members}/${
              this.memberId
          }/validate-deposits`,
          {
            params: payload,
          },
        )
        .then((response) => {
          this.validatedResponse = response.data;
          if (
            this.validatedResponse.totalDepositAmount >
            this.validatedResponse.hardLimitAmount
          ) {
            this.showConfirmModal = true;
            this.showConfirmModalActions = false;
            this.isLoading = false;
            this.$emit('isLoading', false);
            this.modalError = 'make-deposit_form-error_contribution-over-limit';
          } else if (
            this.validatedResponse.totalDepositAmount <
              this.validatedResponse.hardLimitAmount &&
            this.validatedResponse.totalDepositAmount >
              this.validatedResponse.softLimitAmount
          ) {
            this.showConfirmModal = true;
            this.showConfirmModalActions = true;
            this.isLoading = false;
            this.$emit('isLoading', false);
            this.modalError =
              'make-deposit_form-error_contribution-possibly-over-limit';
          } else {
            this.submitDeposit(response.data);
          }
        })
        .catch(() => {
          this.$emit('updateConfirmBeforeClose', false);
          this.isLoading = false;
          // @ts-ignore
          this.showErrorMessage = true;
        });
    },
    submitDeposit(data): void {
      this.validatedResponse.bankId = this.formModel.bankAccount;
      this.canCancelModal = false;
      this.showConfirmModal = false;
      this.isLoading = true;
      this.$emit('isLoading', true);
      let pullDate = '';

      if (!this.isOneTime) {
        pullDate = moment(this.calculatePullDate(), dateFormat).format(datePickerFormat);
      } else {
        pullDate = moment(this.formModel.expectedPostDate, dateFormat).format(datePickerFormat);
      }

      if (this.selectedDeposit) {
        // UC-REST-034 - Update Scheduled Deposit
        api
          .patch(
            `${ACCOUNT_API_SUB_URLS.scheduledDeposits}${V2}/${
              this.selectedDeposit.scheduledDepositId
            }`,
            {
              accountId: toString(this.hsaAccount.accountId),
              bankId: toString(this.selectedDeposit.bankId),
              amount: toNumber(this.formModel.amount),
              taxYear: toString(this.validatedResponse.taxYear),
              pullDate,
              isOneTime: this.isOneTime,
            },
          )
          .then(() => {
            this.refreshTransactions();
            // GTM Event - Update Deposit
            window.dataLayer.push({
              event: 'changeDeposit',
            });
          })
          .catch(() => {
            this.$emit('updateConfirmBeforeClose', false);
            this.canCancelModal = true;
            this.isLoading = false;
            // @ts-ignore
            this.showErrorMessage = true;
          });
      } else {
        // UC-REST-035 - Add Scheduled Deposit
        api
          .post(
            `${MEMBER_API_SUB_URLS.members}/${
              this.memberId
            }/scheduled-deposits`,
            Object.assign(
              {},
              {
                accountId: toString(this.hsaAccount.accountId),
                bankId: toString(this.validatedResponse.bankId),
                amount: toNumber(this.formModel.amount),
                taxYear: toString(this.formModel.taxYear),
                pullDate,
                isOneTime: this.isOneTime,
              },
            ),
          )
          .then(() => {
            this.refreshTransactions();
            if (this.validatedResponse.isOneTime) {
              // GTM Event - Submit Direct Deposit
              window.dataLayer.push({
                event: 'makeDeposit',
              });
            } else {
              // GTM Event - Submit Direct Deposit
              window.dataLayer.push({
                event: 'addDirectDeposit',
              });
            }
          })
          .catch(() => {
            this.canCancelModal = true;
            this.$emit('updateConfirmBeforeClose', false);
            this.isLoading = false;
            // @ts-ignore
            this.showErrorMessage = true;
          });
      }
    },
    refreshTransactions(): void {
      if (this.accountId) {
        this.$store
          .dispatch('getAccountTransactions', this.accountId)
          .then(() => {
            this.isLoading = false;
            this.$emit('isLoading', false);
            this.$emit('updateReset');
            this.showSubmitSuccessMessage = true;
            this.canCancelModal = true;
            this.$emit('doneLoading');
          })
          .catch(() => {
            this.isLoading = false;
            this.$emit('isLoading', false);
            this.canCancelModal = true;
            let refreshMessage;
            if (this.selectedDeposit) {
              refreshMessage = [
                'make-deposit_text_refresh-data-error-message_update-deposit',
                'global_connection-error',
              ];
            } else {
              refreshMessage = [
                'make-deposit_text_refresh-data-error-message_add-deposit',
                'global_connection-error',
              ];
            }
            this.triggerRefreshError({
              message: refreshMessage,
            });
          });
      } else {
        this.isLoading = false;
        this.$emit('isLoading', false);
        this.showSubmitSuccessMessage = true;
        this.$emit('doneLoading');
      }
    },
  },
  components: {
    DatePicker,
    FullscreenSuccess,
    FullscreenError,
    FullscreenLoading,
  },
});
